import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const today = () => new Date().toISOString().split('T')[0];

const ROOM_TYPES = ['General', 'Semi-Private', 'Private', 'ICU', 'OT'];
const ROOM_STATUSES = ['available', 'occupied', 'cleaning', 'maintenance'];

const ROOM_STATUS_COLOR = {
  available:   { bg: '#e6f4ea', color: '#34a853' },
  occupied:    { bg: '#fce8e6', color: '#c5221f' },
  cleaning:    { bg: '#fef3c7', color: '#f9ab00' },
  maintenance: { bg: '#f1f3f4', color: '#9aa0a6' },
};

const ADMISSION_STATUS_COLOR = {
  admitted:   { bg: '#e8f4fd', color: C },
  discharged: { bg: '#f1f3f4', color: '#5f6368' },
};

const EMPTY_ROOM = { roomNo: '', roomType: 'General', floor: '', ratePerDay: '', capacity: '', status: 'available' };
const EMPTY_ADMISSION = { patientId: '', roomId: '', doctorId: '', admissionDate: today(), expectedDischarge: '', diagnosis: '', notes: '' };
const EMPTY_DISCHARGE = { dischargeDate: today(), totalCharges: '', notes: '' };

const inp = { className: 'form-control' };

function F({ label, children, span }) {
  return (
    <div className="form-group" style={span ? { gridColumn: '1/-1' } : {}}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, width }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: width || 520,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: DARK }}>{title}</div>
          <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368' }}
            onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrBanner({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px',
      marginBottom: 14, fontSize: 13 }}>
      {msg}
    </div>
  );
}

export default function HospitalIPD({ role }) {
  const [tab, setTab] = useState('rooms');

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
        {['rooms', 'admissions'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 22px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 13, textTransform: 'capitalize',
            background: tab === t ? C : '#f1f3f4',
            color: tab === t ? '#fff' : '#5f6368',
          }}>
            {t === 'rooms' ? 'Rooms' : 'Admissions'}
          </button>
        ))}
      </div>
      {tab === 'rooms' ? <RoomsTab role={role} /> : <AdmissionsTab role={role} />}
    </div>
  );
}

function RoomsTab({ role }) {
  const [rooms, setRooms]         = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(EMPTY_ROOM);
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');

  const load = useCallback(() => {
    hospitalApi.getRooms().then(d => { if (d) setRooms(d); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(EMPTY_ROOM); setEditId(null); setErr(''); };

  const openAdd = () => { resetForm(); setShowModal(true); };

  const openEdit = (r) => {
    setForm({
      roomNo: r.room_no || '',
      roomType: r.room_type || 'General',
      floor: r.floor || '',
      ratePerDay: r.rate_per_day || '',
      capacity: r.capacity || '',
      status: r.status || 'available',
    });
    setEditId(r.id);
    setShowModal(true);
    setErr('');
  };

  const handleSave = async () => {
    if (!form.roomNo.trim()) { setErr('Room number is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await hospitalApi.updateRoom(editId, form);
      else await hospitalApi.createRoom(form);
      load();
      setShowModal(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this room? This cannot be undone.')) return;
    await hospitalApi.deleteRoom(id);
    load();
  };

  return (
    <div>
      {role !== 'viewer' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Room</button>
        </div>
      )}

      {rooms.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9aa0a6', padding: 48, fontSize: 14 }}>
          No rooms found
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {rooms.map(r => {
            const sc = ROOM_STATUS_COLOR[r.status] || { bg: '#f1f3f4', color: '#5f6368' };
            return (
              <div key={r.id}
                onClick={() => role !== 'viewer' && openEdit(r)}
                style={{
                  background: '#fff', borderRadius: 12, padding: 18,
                  boxShadow: '0 1px 6px rgba(0,0,0,.08)', border: '1px solid #e8eaed',
                  cursor: role !== 'viewer' ? 'pointer' : 'default',
                  transition: 'box-shadow .15s',
                }}
                onMouseEnter={e => { if (role !== 'viewer') e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.13)'; }}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,.08)'}
              >
                <div style={{ fontSize: 28, fontWeight: 800, color: DARK, lineHeight: 1 }}>
                  {r.room_no}
                </div>
                <div style={{ fontSize: 13, color: '#5f6368', marginTop: 4 }}>{r.room_type}</div>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontSize: 12, color: '#5f6368' }}>Floor: <strong style={{ color: DARK }}>{r.floor || '—'}</strong></div>
                  <div style={{ fontSize: 12, color: '#5f6368' }}>Rate/Day: <strong style={{ color: DARK }}>
                    {r.rate_per_day ? '₹' + Number(r.rate_per_day).toLocaleString('en-IN') : '—'}
                  </strong></div>
                  <div style={{ fontSize: 12, color: '#5f6368' }}>Capacity: <strong style={{ color: DARK }}>{r.capacity || '—'}</strong></div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <span style={{ background: sc.bg, color: sc.color, borderRadius: 6,
                    padding: '3px 10px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>
                    {r.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <Modal title={editId ? 'Edit Room' : 'Add Room'} onClose={() => { setShowModal(false); resetForm(); }}>
          <ErrBanner msg={err} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <F label="Room No *">
              <input {...inp} value={form.roomNo} onChange={e => setForm(f => ({ ...f, roomNo: e.target.value }))} />
            </F>
            <F label="Room Type">
              <select {...inp} value={form.roomType} onChange={e => setForm(f => ({ ...f, roomType: e.target.value }))}>
                {ROOM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="Floor">
              <input {...inp} value={form.floor} onChange={e => setForm(f => ({ ...f, floor: e.target.value }))} />
            </F>
            <F label="Rate Per Day (₹)">
              <input {...inp} type="number" min="0" value={form.ratePerDay}
                onChange={e => setForm(f => ({ ...f, ratePerDay: e.target.value }))} />
            </F>
            <F label="Capacity">
              <input {...inp} type="number" min="1" value={form.capacity}
                onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
            </F>
            <F label="Status">
              <select {...inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {ROOM_STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </F>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update Room' : 'Add Room'}
            </button>
            {editId && role === 'admin' && (
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #f5c6c6',
                background: '#fce8e6', color: '#c5221f', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                onClick={() => { handleDelete(editId); setShowModal(false); resetForm(); }}>
                Delete
              </button>
            )}
            <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
              background: '#fff', cursor: 'pointer', fontSize: 13 }}
              onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AdmissionsTab({ role }) {
  const [admissions, setAdmissions]     = useState([]);
  const [patients, setPatients]         = useState([]);
  const [rooms, setRooms]               = useState([]);
  const [doctors, setDoctors]           = useState([]);
  const [filter, setFilter]             = useState('all');
  const [showAdmitModal, setShowAdmit]  = useState(false);
  const [showDischarge, setShowDisch]   = useState(false);
  const [dischargeId, setDischargeId]   = useState(null);
  const [form, setForm]                 = useState(EMPTY_ADMISSION);
  const [editId, setEditId]             = useState(null);
  const [disForm, setDisForm]           = useState(EMPTY_DISCHARGE);
  const [saving, setSaving]             = useState(false);
  const [err, setErr]                   = useState('');
  const [disErr, setDisErr]             = useState('');

  const load = useCallback(() => {
    const statusParam = filter === 'all' ? undefined : filter === 'current' ? 'admitted' : 'discharged';
    hospitalApi.getAdmissions(statusParam).then(d => { if (d) setAdmissions(d); });
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
    hospitalApi.getRooms().then(d => { if (d) setRooms(d); });
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d.filter(doc => doc.status === 'active')); });
  }, []);

  const availableRooms = rooms.filter(r => r.status === 'available');

  const resetForm = () => { setForm(EMPTY_ADMISSION); setEditId(null); setErr(''); };

  const openAdmit = () => { resetForm(); setShowAdmit(true); };

  const openEdit = (a) => {
    setForm({
      patientId: String(a.patient_id || ''),
      roomId: String(a.room_id || ''),
      doctorId: String(a.doctor_id || ''),
      admissionDate: a.admission_date || today(),
      expectedDischarge: a.expected_discharge || '',
      diagnosis: a.diagnosis || '',
      notes: a.notes || '',
    });
    setEditId(a.id);
    setShowAdmit(true);
    setErr('');
  };

  const openDischarge = (id) => {
    setDischargeId(id);
    setDisForm(EMPTY_DISCHARGE);
    setDisErr('');
    setShowDisch(true);
  };

  const handleSave = async () => {
    if (!form.patientId || !form.roomId || !form.doctorId) {
      setErr('Patient, Room, and Doctor are required');
      return;
    }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        patientId: parseInt(form.patientId),
        roomId: parseInt(form.roomId),
        doctorId: parseInt(form.doctorId),
      };
      if (editId) await hospitalApi.updateAdmission(editId, payload);
      else await hospitalApi.createAdmission(payload);
      load();
      setShowAdmit(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDischarge = async () => {
    if (!disForm.dischargeDate) { setDisErr('Discharge date is required'); return; }
    setSaving(true); setDisErr('');
    try {
      await hospitalApi.dischargeAdmission(dischargeId, disForm);
      load();
      setShowDisch(false);
      setDischargeId(null);
    } catch (e) { setDisErr(e.message); }
    finally { setSaving(false); }
  };

  const FILTER_OPTS = [
    { key: 'all', label: 'All' },
    { key: 'current', label: 'Currently Admitted' },
    { key: 'discharged', label: 'Discharged' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {FILTER_OPTS.map(o => (
            <button key={o.key} onClick={() => setFilter(o.key)} style={{
              padding: '6px 16px', borderRadius: 7, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 12,
              background: filter === o.key ? C : '#f1f3f4',
              color: filter === o.key ? '#fff' : '#5f6368',
            }}>
              {o.label}
            </button>
          ))}
        </div>
        {role !== 'viewer' && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openAdmit}>
            + Admit Patient
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: LIGHT }}>
                {['Admission No', 'Patient', 'Room', 'Doctor', 'Admitted On', 'Expected Discharge', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admissions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>No admissions found</td>
                </tr>
              ) : admissions.map((a, i) => {
                const sc = ADMISSION_STATUS_COLOR[a.status] || { bg: '#f1f3f4', color: '#5f6368' };
                return (
                  <tr key={a.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#888' }}>{a.admission_no || '—'}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 600, color: DARK }}>{a.patient_name || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#5f6368' }}>
                      {a.room_no ? `${a.room_no} (${a.room_type || ''})` : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#5f6368' }}>{a.doctor_name ? `Dr. ${a.doctor_name}` : '—'}</td>
                    <td style={{ padding: '9px 12px' }}>{a.admission_date || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>{a.expected_discharge || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ background: sc.bg, color: sc.color, borderRadius: 6,
                        padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {role !== 'viewer' && a.status === 'admitted' && (
                          <button style={{ fontSize: 11, padding: '3px 9px', background: '#e6f4ea',
                            color: '#27ae60', border: '1px solid #b7dfbe', borderRadius: 5, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                            onClick={() => openDischarge(a.id)}>
                            Discharge
                          </button>
                        )}
                        {role !== 'viewer' && (
                          <button style={{ fontSize: 11, padding: '3px 9px', background: LIGHT,
                            color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => openEdit(a)}>
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAdmitModal && (
        <Modal title={editId ? 'Edit Admission' : 'Admit Patient'} onClose={() => { setShowAdmit(false); resetForm(); }} width={540}>
          <ErrBanner msg={err} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <F label="Patient *">
              <select {...inp} value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}>
                <option value="">Select Patient</option>
                {patients.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
              </select>
            </F>
            <F label="Room *">
              <select {...inp} value={form.roomId} onChange={e => setForm(f => ({ ...f, roomId: e.target.value }))}>
                <option value="">Select Room</option>
                {availableRooms.map(r => (
                  <option key={r.id} value={String(r.id)}>
                    {r.room_no} — {r.room_type} {r.floor ? `(Floor ${r.floor})` : ''}
                  </option>
                ))}
              </select>
            </F>
            <F label="Doctor *">
              <select {...inp} value={form.doctorId} onChange={e => setForm(f => ({ ...f, doctorId: e.target.value }))}>
                <option value="">Select Doctor</option>
                {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name}</option>)}
              </select>
            </F>
            <F label="Admission Date">
              <input {...inp} type="date" value={form.admissionDate}
                onChange={e => setForm(f => ({ ...f, admissionDate: e.target.value }))} />
            </F>
            <F label="Expected Discharge">
              <input {...inp} type="date" value={form.expectedDischarge}
                onChange={e => setForm(f => ({ ...f, expectedDischarge: e.target.value }))} />
            </F>
            <F label="Diagnosis" span>
              <textarea {...inp} rows={2} value={form.diagnosis}
                onChange={e => setForm(f => ({ ...f, diagnosis: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </F>
            <F label="Notes" span>
              <textarea {...inp} rows={2} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </F>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update Admission' : 'Admit Patient'}
            </button>
            <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
              background: '#fff', cursor: 'pointer', fontSize: 13 }}
              onClick={() => { setShowAdmit(false); resetForm(); }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showDischarge && (
        <Modal title="Discharge Patient" onClose={() => { setShowDisch(false); setDischargeId(null); }} width={440}>
          <ErrBanner msg={disErr} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <F label="Discharge Date *">
              <input {...inp} type="date" value={disForm.dischargeDate}
                onChange={e => setDisForm(f => ({ ...f, dischargeDate: e.target.value }))} />
            </F>
            <F label="Total Charges (₹)">
              <input {...inp} type="number" min="0" value={disForm.totalCharges}
                onChange={e => setDisForm(f => ({ ...f, totalCharges: e.target.value }))} />
            </F>
            <F label="Notes">
              <textarea {...inp} rows={3} value={disForm.notes}
                onChange={e => setDisForm(f => ({ ...f, notes: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </F>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleDischarge} disabled={saving}>
              {saving ? 'Processing…' : 'Confirm Discharge'}
            </button>
            <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
              background: '#fff', cursor: 'pointer', fontSize: 13 }}
              onClick={() => { setShowDisch(false); setDischargeId(null); }}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
